import { connect } from 'react-redux';
import { Clearance } from '../components';
import { history } from '../../store';
import * as submissionActions from '../../submissions/actions';
import * as clearanceActions from '../actions';
import { SUBMISSION_STATUS } from '../../constants/submission';
import { impersonateBrokerId } from '../../person/actions.js';
import { fetchBrokerages } from '../../brokerages/actions';

/**
 * Dispatches onUpdateBrokerId
 *
 * @param {Function} dispatch
 * @param {String} id
 */
export const onUpdateBrokerId = (dispatch, id) => {
    dispatch(impersonateBrokerId(id));
};

/**
 * Dispatches fetchBrokerages
 *
 * @param {Function} dispatch
 */
export const fetchBrokers = dispatch => {
    dispatch(fetchBrokerages());
};
/**
 * Dispatches fecth clearance
 *
 * @param {Function} dispatch
 * @param {Object} data
 * @param {String} type
 * @param {Object} person
 */
export const onFormSubmit = (dispatch, data, type, user, person) => {
    dispatch(clearanceActions.fetchClearance(data, type, user, person));
};

/**
 * Checks if current lcerance contains macthes
 *
 * @param {Object} clearance
 */
export const isMatchedFound = clearance => {
    return Array.isArray(clearance.data.matches) && clearance.data.matches.length > 0;
};

/**
 * When Clearance passed
 *
 * @param {Function} dispatch
 * @param {Object} submission
 * @param {Object} clearance
 * @param {Function} updateSubmission
 *
 */
export const onPass = (dispatch, submission, clearance, updateSubmission) => {
    dispatch(submissionActions.changeSubmissionStatus(SUBMISSION_STATUS.CREATING));
    updateSubmission(dispatch, submission, clearance);
    history.push('/submission');
    dispatch(submissionActions.submissionClearancePassed(submission.type)); // for amplitude
};

/**
 * When Clearance failed
 *
 * @param {Function} dispatch
 * @param {Object} submission
 * @param {Object} clearance
 * @param {Function} updateSubmission
 *
 */
export const onFail = (dispatch, submission, clearance, updateSubmission) => {
    dispatch(submissionActions.submissionClearanceFailed(submission.type)); // for amplitude
};

/**
 * When Clearance completed with pending result
 *
 * @param {Function} dispatch
 * @param {Object} submission
 * @param {Object} clearance
 * @param {Function} updateSubmission
 *
 */
export const onPending = (dispatch, submission, clearance, updateSubmission) => {
    dispatch(submissionActions.changeSubmissionStatus(SUBMISSION_STATUS.CREATING));
    updateSubmission(dispatch, submission, clearance);
    history.push('/submission');
    dispatch(submissionActions.submissionClearancePending(submission.type)); // for amplitude
};

/**
 * When Clearance completed with matches
 *
 * @param {Function} dispatch
 * @param {Object} submission
 * @param {Object} clearance
 * @param {Function} updateSubmission
 *
 * @todo add Amplitude stuff here
 */
export const onMatch = (dispatch, submission, clearance, updateSubmission) => {
    updateSubmission(dispatch, submission, clearance);
};

/**
 * Report about the error
 *
 * @todo Add logging logic
 *
 * @param {Function} dispatch
 * @param {String} errorMsg
 * @param {Boolean} moveOut
 */
export const onError = (dispatch, errorMsg, moveOut = true) => {
    console.error(errorMsg);

    if (moveOut) {
        history.push('/submissions');
    }
};

/**
 * Check if component has all necessary props to continue
 *
 * @param {Object} props Current props
 * @param {Boolean} toReport Should report about the error or not
 */
export const checkNecessaryProps = (props, toReport = true) => {
    if (!props.submission || !props.clearance) {
        if (toReport) {
            props.onError('Absent submission or clearance');
        }

        return false;
    }

    return true;
};

/**
 * When component was loaded first time we should change the status of current submission
 *
 * @param {Function} dispatch
 */
export const prepareSubmissionStatus = dispatch => {
    dispatch(submissionActions.changeSubmissionStatus(SUBMISSION_STATUS.CLEARANCE));
};

/**
 * Disable params for submission form
 */
export const disableFormParams = () => {
    return {
        primaryInsuredName: { disabled: true },

        primaryInsuredAddress: { disabled: true },
        primaryInsuredAddress2: { disabled: true },
        primaryInsuredCity: { disabled: true },
        primaryInsuredState: { disabled: true },
        primaryInsuredZipcode: { disabled: true },

        projectAddress: { disabled: true },
        projectAddress2: { disabled: true },
        projectCity: { disabled: true },
        projectState: { disabled: true },
        projectZipcode: { disabled: true }
    };
};

/**
 * When clearance completed we should update submisison with form data depending on wheather it's NY or not
 *
 * @param {Function} dispatch
 * @param {Object} submission
 * @param {Object} clearance
 *
 * @returns {Object} payload
 */
export const updateSubmission = (dispatch, submission, clearance) => {
    const submissionFormParams = disableFormParams();
    const updatedSubmission = { ...clearance.formData };

    updatedSubmission.clearanceStatus = clearance.data.status;

    // if submission is for OCP and state is NOT NY
    // nycha is an additional params that is disabled
    // when state is not New York
    if (
        clearance.formData.projectAddress.projectState !== 'New York' &&
        submission.type === 'ocp'
    ) {
        updatedSubmission.nycha = 'false';
        submissionFormParams.nycha = { disabled: true };
    }

    dispatch(submissionActions.changeSubmission(updatedSubmission, submissionFormParams));

    return {
        submission: updatedSubmission,
        submissionFormParams
    };
};

/**
 * If user decides to reset clearance
 *
 * @param {Function} dispatch
 */
export const resetClearance = dispatch => {
    dispatch(clearanceActions.resetClearance());
};

/**
 * Cleans up all data
 *
 * @param {Function} dispatch
 */
export const cleanup = dispatch => {
    dispatch(clearanceActions.resetClearance());
    dispatch(clearanceActions.resetClearanceForm());
};

/**
 * After user selects matched submission we should resolve it depending on it's source (OE/EDGE) and type (OI/OCP)
 *
 * @param {Function} dispatch
 * @param {Object} match
 * @param {Object} user current user. It really should not be here. The only reason why its here because editSubmission requires it.
 *
 * @todo Remove dependency of user after refactoring editSubmission
 */
export const resolveMatched = (dispatch, match, user) => {
    if (match.status === 'emailed') {
        // is EDGE
        dispatch(submissionActions.changeRelatedEntities(match.relatedEntities));
        dispatch(clearanceActions.forceClearancePass());
        return;
    }

    if (match.type === 'oi') {
        dispatch(submissionActions.editSubmission({ id: match.submission_id }, user));
        return;
    }

    if (match.type === 'ocp') {
        dispatch(submissionActions.editSubmission({ id: match.submission_id }, user));
        return;
    }

    onError(dispatch, 'Unhandled situation with processing form', false);
};

/**
 * If user decides to ignore found matched submissions
 *
 * @param {Function} dispatch
 */
export const ignoreMatched = dispatch => {
    dispatch(clearanceActions.forceClearancePass());
};

export const mapStateToProps = state => ({
    submission: state.app.submission,
    clearance: state.clearance,
    user: state.user,
    person: state.person,
    brokers: state.brokerages.items,
    underwriterName: state.underwriter.name,
});

export const mapDispatchToProps = dispatch => ({
    onFormSubmit: (data, type, user, person) => onFormSubmit(dispatch, data, type, user, person),
    isMatchedFound: clearance => isMatchedFound(clearance),
    onPass: (submission, clearance) => onPass(dispatch, submission, clearance, updateSubmission),
    onFail: (submission, clearance) => onFail(dispatch, submission, clearance, updateSubmission),
    onPending: (submission, clearance) =>
        onPending(dispatch, submission, clearance, updateSubmission),
    onMatch: (submission, clearance) => onMatch(dispatch, submission, clearance, updateSubmission),
    onError: (errorMsg, moveOut) => onError(dispatch, errorMsg, moveOut),
    checkNecessaryProps: (props, toReport) => checkNecessaryProps(props, toReport),
    prepareSubmissionStatus: () => prepareSubmissionStatus(dispatch),
    resetClearance: () => resetClearance(dispatch),
    cleanup: () => cleanup(dispatch),
    resolveMatched: (match, user) => resolveMatched(dispatch, match, user),
    ignoreMatched: () => ignoreMatched(dispatch),
    onUpdateBrokerId: (data, type, person) => onUpdateBrokerId(dispatch, data, type, person),
    fetchBrokers: () => fetchBrokers(dispatch)
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Clearance);
